import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/react';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SubscriptionStatus =
  | 'no_subscription' // Card-required trial not yet started — needs Stripe Checkout
  | 'trial'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'expired'
  | 'loading';

export interface ResourceStats {
  lessons: number;
  activities: number;
  presentations: number;
  assessments: number;
  total: number;
  hoursSaved: number;
}

export interface BillingState {
  status: SubscriptionStatus;
  plan: 'monthly' | 'annual' | null;
  planName: string | null;
  amount: number | null;
  trialEndDate: string | null;
  currentPeriodEnd: string | null;
  nextChargeAmount: number | null;
  nextChargeDate: string | null;
  cancelAtPeriodEnd: boolean;
  cancelledAt: string | null;
  reactivatedAt: string | null;
  reactivationResourceCount: number | null;
  referralCredits: number;
  paymentMethodLast4: string | null;
  paymentMethodBrand: string | null;
  resourceStats: ResourceStats | null;
  welcomeBannerDismissed: boolean;
}

interface BillingContextType {
  billing: BillingState;
  loading: boolean;
  /** True when the teacher has full product access. */
  hasAccess: boolean;
  /** Days left in trial, or null if not in trial. */
  trialDaysLeft: number | null;
  refresh: () => Promise<void>;
  startCheckout: (interval: 'monthly' | 'annual') => Promise<void>;
  openPortal: () => Promise<void>;
  cancelSubscription: () => Promise<{ accessUntil: string | null }>;
  undoCancel: () => Promise<void>;
  dismissWelcomeBanner: () => Promise<void>;
}

const DEFAULT_BILLING: BillingState = {
  status: 'loading',
  plan: null,
  planName: null,
  amount: null,
  trialEndDate: null,
  currentPeriodEnd: null,
  nextChargeAmount: null,
  nextChargeDate: null,
  cancelAtPeriodEnd: false,
  cancelledAt: null,
  reactivatedAt: null,
  reactivationResourceCount: null,
  referralCredits: 0,
  paymentMethodLast4: null,
  paymentMethodBrand: null,
  resourceStats: null,
  welcomeBannerDismissed: false,
};

const BillingContext = createContext<BillingContextType>({
  billing: DEFAULT_BILLING,
  loading: true,
  hasAccess: true, // default true to avoid flash of locked state
  trialDaysLeft: null,
  refresh: async () => {},
  startCheckout: async () => {},
  openPortal: async () => {},
  cancelSubscription: async () => ({ accessUntil: null }),
  undoCancel: async () => {},
  dismissWelcomeBanner: async () => {},
});

export function useBilling() {
  return useContext(BillingContext);
}

// ── Provider ──────────────────────────────────────────────────────────────────

export function BillingProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded } = useUser();
  const [billing, setBilling] = useState<BillingState>(DEFAULT_BILLING);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch('/api/billing/status');
      if (res.ok) {
        const data = await res.json();
        setBilling({
          status: data.status ?? 'no_subscription',
          plan: data.plan ?? null,
          planName: data.planName ?? null,
          amount: data.amount ?? null,
          trialEndDate: data.trialEndDate ?? null,
          currentPeriodEnd: data.currentPeriodEnd ?? null,
          nextChargeAmount: data.nextChargeAmount ?? null,
          nextChargeDate: data.nextChargeDate ?? null,
          cancelAtPeriodEnd: data.cancelAtPeriodEnd ?? false,
          cancelledAt: data.cancelledAt ?? null,
          reactivatedAt: data.reactivatedAt ?? null,
          reactivationResourceCount: data.reactivationResourceCount ?? null,
          referralCredits: data.referralCredits ?? 0,
          paymentMethodLast4: data.paymentMethodLast4 ?? null,
          paymentMethodBrand: data.paymentMethodBrand ?? null,
          resourceStats: data.resourceStats ?? null,
          welcomeBannerDismissed: data.welcomeBannerDismissed ?? false,
        });
      }
    } catch {
      // Billing service unavailable — default to granting access (graceful failure)
      setBilling(prev => ({ ...prev, status: prev.status === 'loading' ? 'active' : prev.status }));
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Fetch billing status on load (card-required trial: no auto-start)
  useEffect(() => {
    if (!isLoaded || !user) {
      if (isLoaded) setLoading(false);
      return;
    }
    fetchStatus();
  }, [isLoaded, user, fetchStatus]);

  // Refresh billing when this tab regains focus (covers the checkout-new-tab return path)
  useEffect(() => {
    const handler = () => { if (document.visibilityState === 'visible') fetchStatus(); };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [fetchStatus]);

  /**
   * Cross-tab push: when the checkout tab lands on success/cancel URL it
   * posts a 'billing-updated' message on this channel.  The original tab
   * hears it immediately and refreshes status without waiting for a focus
   * event — so the teacher sees their new plan the moment they look back
   * at their original tab.
   */
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('teachbud-billing');
    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === 'billing-updated') fetchStatus();
    };
    ch.addEventListener('message', onMessage);
    return () => { ch.removeEventListener('message', onMessage); ch.close(); };
  }, [fetchStatus]);

  // Grant access while loading; block when expired OR teacher hasn't started a trial yet
  const hasAccess =
    billing.status === 'loading' ||
    (billing.status !== 'expired' && billing.status !== 'no_subscription');

  const trialDaysLeft: number | null = (() => {
    if (billing.status !== 'trial' || !billing.trialEndDate) return null;
    const diff = new Date(billing.trialEndDate).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  })();

  /**
   * Start a Stripe Checkout session.
   * Accepts `'monthly' | 'annual'` — the server resolves the Price ID.
   * The browser never supplies a price ID or dollar amount.
   *
   * Opens checkout in a NEW tab so the teacher's original tab is preserved.
   * When Stripe redirects back to successUrl / cancelUrl, the destination
   * page posts a BroadcastChannel message ('billing-updated') which this
   * provider hears and uses to refresh billing status on the original tab.
   */
  const startCheckout = useCallback(async (interval: 'monthly' | 'annual') => {
    const res = await fetch('/api/billing/checkout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ interval }),
    });
    if (!res.ok) throw new Error((await res.json()).error ?? 'Checkout failed');
    const data = await res.json();
    // Open Stripe Checkout (or Customer Portal) in a new tab.
    // noopener,noreferrer: the checkout tab gets no reference back to this
    // window, which is correct — we communicate through BroadcastChannel.
    window.open(data.url, '_blank', 'noopener,noreferrer');
  }, []);

  const openPortal = useCallback(async () => {
    const res = await fetch('/api/billing/portal', { method: 'POST' });
    if (!res.ok) throw new Error((await res.json()).error ?? 'Portal unavailable');
    const { url } = await res.json();
    window.location.href = url;
  }, []);

  const cancelSubscription = useCallback(async () => {
    const res = await fetch('/api/billing/cancel', { method: 'POST' });
    if (!res.ok) throw new Error((await res.json()).error ?? 'Cancellation failed');
    const data = await res.json();
    await fetchStatus();
    return { accessUntil: data.accessUntil ?? null };
  }, [fetchStatus]);

  const undoCancel = useCallback(async () => {
    const res = await fetch('/api/billing/undo-cancel', { method: 'POST' });
    if (!res.ok) throw new Error((await res.json()).error ?? 'Failed');
    await fetchStatus();
  }, [fetchStatus]);

  const dismissWelcomeBanner = useCallback(async () => {
    setBilling(prev => ({ ...prev, welcomeBannerDismissed: true }));
    await fetch('/api/billing/dismiss-welcome-banner', { method: 'POST' }).catch(() => {});
  }, []);

  return (
    <BillingContext.Provider value={{
      billing,
      loading,
      hasAccess,
      trialDaysLeft,
      refresh: fetchStatus,
      startCheckout,
      openPortal,
      cancelSubscription,
      undoCancel,
      dismissWelcomeBanner,
    }}>
      {children}
    </BillingContext.Provider>
  );
}
